<template>
  <div
    v-if="artistInfo !== null"
    ref="root"
    class="h-100dvh overflow-y-auto"
    @scroll="updateScrollTop"
  >
    <transition
      enter-active-class="transition ease-out duration-200 transform"
      enter-from-class="opacity-0 translate-x-full"
      enter-to-class="opacity-100 translate-x-0"
      leave-active-class="transition ease-in duration-200 transform"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <ArtistMobilePanel
        v-if="isMobile && route.hash.length !== 0 && mobilePanelProps"
        :card-contents="mobilePanelProps.cardContents"
        :has-next-page="mobilePanelProps.hasNextPage"
        :is-loading="mobilePanelProps.isLoading"
        :title="mobilePanelProps.title"
        @fetch-artworks-by-artwork-type="
          (e) => {
            fetchArtworksByArtworkType(e)
          }
        "
        @fetch-artworks-by-design-group="
          (e) => {
            fetchArtworksByDesignGroup(e)
          }
        "
      />
    </transition>
    <DefaultHeader />
    <div
      v-if="artistInfo.banner !== null"
      class="h-200px bg-cover bg-bottom md:h-440px"
      :style="{ backgroundImage: `url(${artistInfo.banner})` }"
    />
    <div
      class="flex flex-col items-center px-24px"
      :class="{
        'mt-30px md:mt-60px': artistInfo.banner === null,
        '-mt-50px': artistInfo.banner !== null,
      }"
    >
      <img
        alt="avatar"
        class="h-100px w-100px border-1 border-bg-gray rounded-full"
        :src="artistInfo.avatar"
      />
      <p class="mt-24px title-14-h20-b text-primary-80">{{ artistInfo.name }}</p>
      <div class="mt-12px flex items-center justify-center gap-16px">
        <i
          v-for="({ type, url }, index) in artistInfo.socialMedia"
          :key="index"
          class="cursor-pointer text-24px text-primary-80"
          :class="socialMediaWithIcons[type]"
          @click="
            () => {
              pushGA4ButtonClick(
                `social media type:${type} artist: ${artistInfo?.name ?? 'unknown'}`,
              )
              redirectUrlWithNewWindow(url)
            }
          "
        />
      </div>
      <p
        class="mt-12px whitespace-pre-wrap text-center content-12-h16-r md:content-14-h24-r text-primary-60"
      >
        {{ artistInfo.introduction }}
      </p>
      <a
        class="mt-12px flex flex-col cursor-pointer items-center justify-center rounded-full bg-light-pink px-25px py-8px content-12-h16-r md:flex-row md:gap-16px"
        @click="
          $router.push({
            name: Route.DesignArtistPage,
            params: { idOrHandle: artistInfo.id },
          })
        "
      >
        <p class="text-deep-brown">
          {{ $t('btn.go_design_artist_page', { name: artistInfo.name }) }}
        </p>
        <p class="text-brand-blush-pink">{{ $t('btn.try_now') }}→</p>
      </a>
    </div>
    <hr class="mt-32px border-1px border-primary-10" />
    <div v-if="isPageLoaded" class="flex justify-center gap-40px pt-16px md:pt-32px">
      <!--   side nav   -->
      <div
        v-if="isDesktop"
        class="sticky top-112px h-fit w-176px flex flex-shrink-0 flex-col gap-24px"
      >
        <div v-if="designGroupToShow.length > 0" class="flex flex-col gap-8px">
          <p class="title-14-h16-m text-primary-80">
            {{ $t('message.gallery-series')
            }}<span class="content-11-h16-r">({{ $t('message.gallery-series_describe') }})</span>
          </p>
          <hr class="border-1px border-primary-40" />
          <a
            v-for="(item, index) in designGroupToShow"
            :key="index"
            class="flex cursor-pointer gap-4px"
            @click="scrollToSection(item.type)"
          >
            <img :alt="item.type" class="h-24px w-24px" :src="item.thumbnailUrl" />
            <p class="title-12-h24-m text-primary-80">
              {{ $t(`design-type.${item.type}`) }}
            </p>
          </a>
        </div>
        <div
          v-if="![...artworksByArtworkType.values()].every((item) => item.totalCount === 0)"
          class="flex flex-col gap-8px"
        >
          <p class="title-14-h16-m text-primary-80">{{ $t('message.material_for_creation') }}</p>
          <hr class="border-1px border-primary-40" />
          <a
            v-for="[key, _] in artworksByArtworkType"
            :key="key"
            class="flex cursor-pointer gap-4px text-primary-80"
            @click="scrollToSection(key)"
          >
            <template v-if="(artworksByArtworkType.get(key)?.content ?? []).length > 0">
              <i class="text-24px" :class="artworksHardCode.get(key)?.icon" />
              <p class="title-12-h24-m">
                {{ artworksHardCode.get(key)?.i18n }}
              </p>
            </template>
          </a>
        </div>
      </div>
      <!--   main section   -->
      <div class="w-full flex flex-col" :style="{ maxWidth: `${CARD_CONTAINER_MAX_WIDTH}px` }">
        <Component
          :is="currentCardContainer"
          v-for="(designGroup, designGroupIndex) in designGroupToShow"
          :id="designGroup.type"
          :key="designGroupIndex"
          :card-content="collagesByDesignGroup.get(designGroup.type) ?? []"
          :expand-handle="designGroup.type"
          :has-next-page="
            pageInfoByDesignGroup.get(designGroup.type)?.pageInfo?.hasNextPage ?? false
          "
          :is-loading="pageInfoByDesignGroup.get(designGroup.type)?.isLoading ?? false"
          :title="$t(`design-type.${designGroup.type}`)"
          :total-count="pageInfoByDesignGroup.get(designGroup.type)?.totalCount"
          @expend="fetchArtworksByDesignGroup(designGroup.type)"
        />
        <div v-for="[key, value] in artworksByArtworkType" :key="key">
          <Component
            :is="currentCardContainer"
            v-if="value.content.length > 0"
            :id="key"
            :card-content="value.content"
            :expand-handle="key"
            :has-next-page="value.pageInfo?.hasNextPage ?? false"
            :is-loading="value.isLoading"
            :title="artworksHardCode.get(key)?.i18n ?? ''"
            :total-count="value.totalCount"
            @expend="fetchArtworksByArtworkType(key)"
          />
        </div>
      </div>
    </div>
    <LoadingSpinner v-else class="mx-auto my-32px" is-loading />
    <p
      v-if="designGroupToShow.length === 0 && artworksByArtworkType.size === 0"
      class="my-32px text-center title-14-h16-m text-primary-80"
    >
      {{ $t('panel.tab.no_result') }}
    </p>
    <!--  todo(chili): 拆 scrollTop component  -->
    <div
      v-if="scrollTop > 300 && isDesktop"
      class="fixed bottom-32px right-32px h-56px w-56px flex cursor-pointer items-center justify-center rounded-full bg-brand-gray-purple-tint hover:bg-brand-gray-purple"
      @click="
        () => {
          if (root !== null) {
            root.scrollTo({
              top: 0,
              behavior: 'smooth',
            })
          }
        }
      "
    >
      <i class="icon-arrow-up text-40px text-white" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { t } from 'i18next'
import { storeToRefs } from 'pinia'
import { useQuery } from 'villus'
import { type Component, computed, onMounted, ref, type UnwrapRef, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ArtistDesktopCardContainer from '@/components/artistPage/ArtistDesktopCardContainer.vue'
import ArtistMobileCardContainer from '@/components/artistPage/ArtistMobileCardContainer.vue'
import ArtistMobilePanel from '@/components/artistPage/ArtistMobilePanel.vue'
import {
  currentHashType,
  isInArtworkType,
  isInDesignGroupType,
} from '@/components/artistPage/expandTypeCheck'
import {
  type DisplayCardContent,
  type MultiLayersPreviewCollageDisplayCardContent,
} from '@/components/DisplayCards/DisplayCard'
import {
  buildBackgroundDisplayCardContent,
  buildCollageDisplayCardContent,
  buildStickerGroupDisplayCardContent,
} from '@/components/DisplayCards/DisplayCardContentBuilder'
import DefaultHeader from '@/components/layout/DefaultHeader.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { designGroupTypeToNewIndicator } from '@/data/hardCode'
import { getProductDisplay } from '@/data/useNewProductDisplay'
import {
  ArtistInfoDocument,
  ArtworksForArtistPageDocument,
  type ArtworksForArtistPageQueryVariables,
  ArtworkType,
  type DesignGroupType,
  type PageInfo,
  SocialMediaType,
} from '@/graphql/creator-platform/generated'
import { backendLocation } from '@/graphql/utils'
import { Route } from '@/router/route'
import { type DesignGroup, useDesignGroupStore } from '@/store/designGroupStore'
import { useLayoutStore } from '@/store/layoutStore'
import { defaultAvatar } from '@/utils/common'
import { pushGA4ButtonClick } from '@/utils/googleAnalytics'
import { setMetadata } from '@/utils/openGraphMeta'
import { redirectUrlWithNewWindow } from '@/utils/redirect'
import { type PropTypeOf } from '@/utils/type.util'
import { convertUserI18ns } from '@/utils/userI18ns'

const router = useRouter()
const route = useRoute()
const designGroupStore = useDesignGroupStore()
const { isDesktop, isMobile } = storeToRefs(useLayoutStore())
// card-width 240 * 4 + gap 8px * 3
const CARD_CONTAINER_MAX_WIDTH = 984

const artistInfo = ref<null | {
  avatar: string
  banner: null | string
  id: string
  introduction: string
  name: string
  socialMedia: Array<{ type: SocialMediaType; url: string }>
}>(null)
const idOrHandle = computed((): string => {
  if (route.params['id'] === undefined) return ''
  return route.params['id'].toString()
})

onMounted(async () => {
  await setArtistInfo()
  if (artistInfo.value === null) {
    await router.replace(Route.Root)
  } else {
    setMetadata('og:image', artistInfo.value.avatar)
    setMetadata('og:title', `${artistInfo.value.name} X RhinoShield`)
    setMetadata('og:description', artistInfo.value.introduction)
  }
  await designGroupStore.init()
  const fetchPromises = designGroupStore.designGroups.map(async (designGroup) => {
    await fetchArtworksByDesignGroup(designGroup.type)
  })
  await Promise.all([
    ...fetchPromises,
    fetchArtworksByArtworkType(ArtworkType.StickerGroup),
    fetchArtworksByArtworkType(ArtworkType.Background),
  ])
  initView()
  isPageLoaded.value = true
})

const root = useTemplateRef<HTMLElement>('root')
const scrollTop = ref(0)
function updateScrollTop(): void {
  scrollTop.value = root.value?.scrollTop ?? 0
}
const isPageLoaded = ref(false)
function scrollToSection(id: string): void {
  const targetElement = document.getElementById(id)
  if (targetElement !== null && root.value !== null) {
    const elementPosition = targetElement.getBoundingClientRect().top
    const offsetPosition = root.value.scrollTop + elementPosition - 112
    root.value.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })
  }
}

async function setArtistInfo(): Promise<void> {
  const { data, error } = await useQuery({
    query: ArtistInfoDocument,
    variables: {
      idOrHandle: idOrHandle.value,
    },
  })
  const artistData =
    data.value?.artistForDitto !== undefined && data.value.artistForDitto !== null
      ? convertUserI18ns(data.value.artistForDitto)
      : null
  if (artistData === null || error.value !== null) {
    return
  }
  const socialMediaList: SocialMediaType[] = [
    SocialMediaType.Facebook,
    SocialMediaType.Instagram,
    SocialMediaType.Threads,
    SocialMediaType.Twitter,
    SocialMediaType.Youtube,
  ]
  const socialMedia: NonNullable<UnwrapRef<typeof artistInfo>>['socialMedia'] = socialMediaList
    .map((type) => {
      const item = artistData.socialMedia.find((item) => item.type === type)
      return {
        type,
        url: item?.url ?? null,
      }
    })
    .filter((item): item is { type: SocialMediaType; url: string } => item.url !== null)
  artistInfo.value = {
    id: artistData.id,
    name: artistData.name,
    avatar: artistData.avatar ?? defaultAvatar,
    banner: artistData.banner ?? null,
    introduction: artistData.introduction,
    socialMedia,
  }
}

const socialMediaWithIcons: Record<SocialMediaType, string> = {
  [SocialMediaType.Instagram]: 'icon-instagram',
  [SocialMediaType.Facebook]: 'icon-facebook',
  [SocialMediaType.Threads]: 'icon-threads',
  [SocialMediaType.Twitter]: 'icon-twitter',
  [SocialMediaType.Youtube]: 'icon-youtube',
}

const collagesByDesignGroup = ref(
  new Map<DesignGroupType, MultiLayersPreviewCollageDisplayCardContent[]>(),
)
const pageInfoByDesignGroup = ref(
  new Map<DesignGroupType, { isLoading: boolean; pageInfo?: PageInfo; totalCount: number }>(),
)

const artworksByArtworkType = ref(
  new Map<
    ArtworkType.Background | ArtworkType.StickerGroup,
    { content: DisplayCardContent[]; isLoading: boolean; pageInfo?: PageInfo; totalCount: number }
  >(),
)
const artworksHardCode = new Map([
  [ArtworkType.Background, { icon: 'icon-background', i18n: t('panel.function.bg') }],
  [ArtworkType.StickerGroup, { icon: 'icon-sticker', i18n: t('panel.function.sticker') }],
])

async function fetchArtworksByDesignGroup(designGroupType: DesignGroupType): Promise<void> {
  if (artistInfo.value === null) return
  const variables: ArtworksForArtistPageQueryVariables = {
    creatorId: artistInfo.value.id,
    designGroupType,
    location: backendLocation,
    artworkType: ArtworkType.Collage,
    cursorPaging: {
      first: 12,
    },
  }
  const existingValue = pageInfoByDesignGroup.value.get(designGroupType)
  if (existingValue !== undefined) {
    pageInfoByDesignGroup.value.set(designGroupType, { ...existingValue, isLoading: true })
  }
  const pageInfo = pageInfoByDesignGroup.value.get(designGroupType)?.pageInfo
  if (pageInfo !== undefined && typeof pageInfo.endCursor === 'string') {
    variables.cursorPaging = {
      first: 16,
      after: pageInfo.endCursor,
    }
  }
  const { data, error } = await useQuery({
    query: ArtworksForArtistPageDocument,
    variables,
  })

  if (error.value !== null || data.value === null) {
    console.error(`Error fetching data for designGroupType ${designGroupType}:`, error.value)
    return
  }

  const display = await getProductDisplay(designGroupTypeToNewIndicator[designGroupType])
  const edges = data.value.artworksForDitto.edges
  if (edges.length === 0) return
  for (const edge of edges) {
    if (edge.node.collage === null || edge.node.collage === undefined) continue
    const cardContent = await buildCollageDisplayCardContent(edge.node.collage, {
      type: 'multi-layer',
      display,
    })
    if (!collagesByDesignGroup.value.has(designGroupType)) {
      collagesByDesignGroup.value.set(designGroupType, [])
    }
    if (cardContent === null) continue
    collagesByDesignGroup.value.get(designGroupType)?.push(cardContent)
  }
  pageInfoByDesignGroup.value.set(designGroupType, {
    pageInfo: data.value.artworksForDitto.pageInfo,
    isLoading: false,
    totalCount: data.value.artworksForDitto.totalCount,
  })
}

const designGroupToShow = ref<DesignGroup[]>([])
function initView(): void {
  for (const designGroup of designGroupStore.designGroups) {
    if (collagesByDesignGroup.value.get(designGroup.type) === undefined) continue
    designGroupToShow.value.push(designGroup as DesignGroup)
  }
}

async function fetchArtworksByArtworkType(
  artworkType: ArtworkType.Background | ArtworkType.StickerGroup,
): Promise<void> {
  if (artistInfo.value === null) return
  const currentValue = artworksByArtworkType.value.get(artworkType) ?? {
    content: [],
    isLoading: true,
    totalCount: 0,
  }
  if (artworksByArtworkType.value.has(artworkType)) {
    currentValue.isLoading = true
  } else {
    artworksByArtworkType.value.set(artworkType, currentValue)
  }
  const variables: ArtworksForArtistPageQueryVariables = {
    creatorId: artistInfo.value.id,
    location: backendLocation,
    artworkType: artworkType,
    cursorPaging: {
      first: 12,
    },
  }
  if (currentValue.pageInfo !== undefined && typeof currentValue.pageInfo.endCursor === 'string') {
    variables.cursorPaging = {
      first: 16,
      after: currentValue.pageInfo.endCursor,
    }
  }

  const { data, error } = await useQuery({
    query: ArtworksForArtistPageDocument,
    variables,
  })
  if (error.value !== null || data.value === null) {
    console.error(`Error fetching data for artworkType ${artworkType}:`, error.value)
    return
  }
  for (const { node } of data.value.artworksForDitto.edges) {
    switch (artworkType) {
      case ArtworkType.Background: {
        if (node.background === null || node.background === undefined) break
        const display = await getProductDisplay(
          designGroupTypeToNewIndicator[node.background.designGroupType],
        )
        const result = await buildBackgroundDisplayCardContent(node.background, {
          type: 'multi-layer',
          display,
        })
        if (result !== null) {
          currentValue.content.push(result)
        }
        break
      }
      case ArtworkType.StickerGroup: {
        if (node.stickerGroup === null || node.stickerGroup === undefined) break
        const result = await buildStickerGroupDisplayCardContent(node.stickerGroup)
        if (result !== null) {
          currentValue.content.push(result)
        }
        break
      }
    }
  }
  currentValue.totalCount = data.value.artworksForDitto.totalCount
  currentValue.pageInfo = data.value.artworksForDitto.pageInfo
  currentValue.isLoading = false
}

const mobilePanelProps = computed((): PropTypeOf<typeof ArtistMobilePanel> | undefined => {
  const type = currentHashType(route.hash)
  if (type === undefined) return
  if (isInDesignGroupType(type)) {
    return {
      cardContents: collagesByDesignGroup.value.get(type) ?? [],
      hasNextPage: pageInfoByDesignGroup.value.get(type)?.pageInfo?.hasNextPage ?? false,
      isLoading: pageInfoByDesignGroup.value.get(type)?.isLoading ?? false,
      title: t(`design-type.${type}`),
    }
  } else if (isInArtworkType(type)) {
    if (type === ArtworkType.Collage) return undefined
    return {
      cardContents: artworksByArtworkType.value.get(type)?.content ?? [],
      hasNextPage: artworksByArtworkType.value.get(type)?.pageInfo?.hasNextPage ?? false,
      isLoading: artworksByArtworkType.value.get(type)?.isLoading ?? false,
      title: artworksHardCode.get(type)?.i18n ?? '',
    }
  }
  return undefined
})

const currentCardContainer = computed((): Component => {
  return isMobile.value ? ArtistMobileCardContainer : ArtistDesktopCardContainer
})
</script>
